/**
 * Column-aware timescale helpers for the SDK sheet layout.
 *
 * `ColumnTimescale` measures a row period against the real column index
 * (`@tento-core/column-index`), where column spans vary (months differ in
 * length, weeks straddle month boundaries). The pure timescale math —
 * ordering, span / stack counts, `RowRenderMode` — lives in
 * `@tento/lona/sheets` (`Timescale`, `RowRenderMode`).
 */

import { PartialDate } from "@tento-chrono";
import { ColumnIndex } from "@tento-core/column-index";

/**
 * Column arithmetic for a row's timescale: how many sheet columns one row
 * period spans under a column-index mapper.
 */
export namespace ColumnTimescale {
  /**
   * Calculate how many sheet columns a row period spans using the index mapper.
   *
   * Uses actual column indices from the mapper rather than a naive
   * approximation, since column spans vary (months have different numbers of
   * days, weeks can span month boundaries).
   */
  export function calculateSpanSize(
    rowPd: PartialDate,
    mapper: ColumnIndex.Mapper,
  ): number {
    const startKey = ColumnIndex.Key.fromPartial(
      PartialDate.fromDate(rowPd.start),
      "lower",
    );
    const startResolved = mapper.resolveKeyToDefault(startKey, "lower");

    const lastDay = rowPd.end.addDays(-1);
    const endKey = ColumnIndex.Key.fromPartial(
      PartialDate.fromDate(lastDay),
      "upper",
    );
    const endResolved = mapper.resolveKeyToDefault(endKey, "upper");

    return endResolved.offset - startResolved.offset + 1;
  }
}
